"use client";

import React, { useState, useEffect } from "react";
import { Section } from "@/components/ui/section";
import { m as motion, AnimatePresence } from "framer-motion";
import { SKILLS_DATA, SECTION_METADATA } from "@/lib/data";

interface SkillCategory {
  title: string;
  icon: string;
  skills: Array<{ name: string; level: number }>;
}

const SkillsCardMobile = ({
  category,
  index,
  isSelected,
  onClick,
}: {
  category: SkillCategory;
  index: number;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-emerald-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-cyan-500",
  ];
  const colorClass = colors[index % colors.length];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ 
        duration: 0.4,
        delay: index * 0.05,
        ease: "easeOut" 
      }}
      onClick={onClick}
      className={`p-4 rounded-xl border transition-all duration-300 transform-gpu backface-visibility-hidden ${
        isSelected
          ? "border-primary bg-card/80 shadow-xl shadow-primary/10"
          : "border-border/40 bg-card/20"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
              isSelected
                ? `${colorClass} animate-pulse scale-125 shadow-[0_0_8px_rgba(var(--primary),0.5)]`
                : "bg-muted-foreground/30"
            }`}
          />
          <h3
            className={`text-sm font-bold tracking-tight transition-colors ${
              isSelected ? "text-primary" : "text-foreground"
            }`}
          >
            {category.title}
          </h3>
        </div>
        <span className="text-[9px] font-mono font-bold text-muted-foreground/30 uppercase tracking-[0.15em]">
          {category.skills.length} Items
        </span>
      </div>

      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-5 space-y-4 overflow-hidden"
          >
            <div className="grid grid-cols-1 gap-3 px-1">
              {category.skills.map((skill, idx) => (
                <div key={idx} className="space-y-1.5">
                  <div className="flex justify-between items-end">
                    <span className="text-xs font-bold text-muted-foreground/90">
                      {skill.name}
                    </span>
                    <span className="text-[9px] font-mono font-black text-primary/60">
                      {skill.level}%
                    </span>
                  </div>
                  <div className="h-1 bg-secondary/30 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${skill.level}%` }}
                      className={`h-full ${colorClass} opacity-60 rounded-full`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Skills = React.memo(({ data }: { data?: any }) => {
  const skillCategories: SkillCategory[] = data || SKILLS_DATA;
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  // Prevent hydration mismatch flickering
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <Section id="skills" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16 md:space-y-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center space-y-4 md:space-y-6 transform-gpu backface-visibility-hidden"
        >
          <h2 className="text-[9px] md:text-xs font-mono uppercase tracking-[0.4em] text-muted-foreground opacity-70">
            {SECTION_METADATA.skills.sectionNumber} /{" "}
            {SECTION_METADATA.skills.sectionLabel}
          </h2>
          <h3 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight px-2">
            {SECTION_METADATA.skills.title}
          </h3>
          <p className="text-muted-foreground/80 max-w-2xl mx-auto text-xs sm:text-base md:text-lg leading-relaxed px-4">
            {SECTION_METADATA.skills.description}
          </p>
        </motion.div>

        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillCategories.map((category, index) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.5,
                delay: index * 0.1,
                ease: [0.21, 0.47, 0.32, 0.98] 
              }}
              onClick={() => {
                if (!isHydrated) return;
                setSelectedCategory(
                  selectedCategory === category.title ? null : category.title
                );
              }}
              className={`group p-6 rounded-2xl border cursor-pointer transition-all duration-300 relative overflow-hidden transform-gpu backface-visibility-hidden ${
                selectedCategory === category.title
                  ? "border-primary bg-card/70 shadow-lg shadow-primary/20 scale-[1.02]"
                  : "border-border/50 bg-card/30 hover:bg-card/50 hover:border-primary/20 hover:-translate-y-1"
              }`}
            >
              <div className="space-y-4 relative z-10">
                <motion.h3
                  className={`text-lg font-semibold transition-colors ${
                    selectedCategory === category.title
                      ? "text-primary"
                      : "text-foreground group-hover:text-primary"
                  }`}
                  animate={
                    selectedCategory === category.title ? { x: 5 } : { x: 0 }
                  }
                >
                  {category.title}
                </motion.h3>

                <AnimatePresence mode="wait">
                  {selectedCategory === category.title ? (
                    <motion.div
                      key="expanded"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="space-y-3 overflow-hidden"
                    >
                      {category.skills.map((skill, idx) => (
                        <motion.div
                          key={skill.name}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          onMouseEnter={() => setHoveredSkill(skill.name)}
                          onMouseLeave={() => setHoveredSkill(null)}
                          className="space-y-1"
                        >
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-foreground">
                              {skill.name}
                            </span>
                            <AnimatePresence>
                              {hoveredSkill === skill.name && (
                                <motion.span
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.8 }}
                                  className="text-sm font-bold text-primary"
                                >
                                  {skill.level}%
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </div>
                          <div className="h-1.5 bg-secondary/30 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${skill.level}%` }}
                              transition={{ duration: 0.8, delay: idx * 0.05 }}
                              className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full"
                            />
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="collapsed"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-wrap gap-2"
                    >
                      {category.skills.map((skill) => (
                        <span
                          key={skill.name}
                          className="px-3 py-1 text-xs font-medium rounded-full bg-secondary/50 text-secondary-foreground border border-border/40 hover:border-primary/40 hover:bg-secondary transition-colors"
                        >
                          {skill.name}
                        </span>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile View */}
        <div className="md:hidden flex flex-col gap-3 px-4">
          {skillCategories.map((category, index) => (
            <SkillsCardMobile
              key={category.title}
              category={category}
              index={index}
              isSelected={selectedCategory === category.title}
              onClick={() => {
                if (!isHydrated) return;
                setSelectedCategory(
                  selectedCategory === category.title ? null : category.title
                );
              }}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center pt-4 md:pt-8"
        >
          <p className="text-[10px] md:text-sm text-muted-foreground italic px-6">
            {selectedCategory
              ? "Tap again to collapse • Proficiency levels shown"
              : "Tap any category to explore detailed skill levels"}
          </p>
        </motion.div>
      </div>
    </Section>
  );
});

Skills.displayName = "Skills";
export default Skills;
